import { DownloadIcon, PlusIcon, UpdateIcon } from '@radix-ui/react-icons';
import { Box, Flex, Heading } from '@radix-ui/themes';
import { MachineContextHelper } from 'classes/helpers/machine-context.helper';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { CommonChecklist } from 'components/common/checklist';
import { CommonDetails } from 'components/common/details';
import { CommonDialog } from 'components/common/dialogs';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonCheckboxInput } from 'components/common/form/checkbox';
import { CommonDateInput } from 'components/common/form/date';
import { CommonFormGrid } from 'components/common/form/grid';
import { CommonSearchInput } from 'components/common/form/search';
import { CommonSelectInput } from 'components/common/form/select';
import { CommonSliderInput } from 'components/common/form/slider';
import { CommonTextInput } from 'components/common/form/text';
import { HINT_DELIMITER } from 'components/common/form/text-hint';
import { JsonViewer } from 'components/common/json/json-viewer';
import { PrintMetric } from 'components/sections/admin-portal/machine-models/metrics/print-metric';
import { SummarizeModelMetrics } from 'components/sections/admin-portal/machine-models/metrics/summarize-model-metrics';
import { HistogramGroup } from 'components/sections/admin-portal/machine-models/preview-plots/histogram-group';
import {
  PlotGroupStd,
  PlotMode,
} from 'components/sections/admin-portal/machine-models/preview-plots/plot-group-std';
import { useMachinesPortalStore } from 'components/sections/admin-portal/machines/store/use-machines-portal-store';
import env from 'config';
import { MachineModelsContext } from 'contexts/admin/machine-models.context';
import {
  endOfHour,
  endOfToday,
  isBefore,
  max,
  parseISO,
  startOfToday,
  subMonths,
} from 'date-fns';
import { format } from 'date-fns-tz';
import { t } from 'i18next';
import { IFullDialog } from 'interfaces/i-dialogs';
import { ALLOWED_MODEL_TYPES } from 'interfaces/i-models';
import { FT_TO_INCHES } from 'lib_ts/classes/math.utilities';
import { MiscHelper } from 'lib_ts/classes/misc.helper';
import { ModelHelper } from 'lib_ts/classes/model.helper';
import {
  LOCAL_DATETIME_FORMAT_SHORT,
  LOCAL_TIMEZONE,
} from 'lib_ts/enums/datetime.enums';
import { BallType } from 'lib_ts/enums/machine.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { IEvalModelResult } from 'lib_ts/interfaces/modelling/i-eval-models';
import { IGatherSummary } from 'lib_ts/interfaces/modelling/i-gather-shot-data';
import { IMachineModel } from 'lib_ts/interfaces/modelling/i-machine-model';
import { ITrainModelsRequest } from 'lib_ts/interfaces/modelling/i-train-model';
import { first } from 'lodash';
import {
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { AdminMachineModelsService } from 'services/admin/machine-models.service';

const COMPONENT_NAME = 'MachineModelCreationDialog';

const BALL_TYPE_OPTIONS = Object.values(BallType).map((m) => ({
  label: m,
  value: m,
}));

enum Step {
  Creation,
  Review,
}

interface IProps {
  /** used for copying details from */
  refModel?: IMachineModel;
  onClose: (refresh: boolean) => void;
}

export const MachineModelCreationDialog: FC<IProps> = (props) => {
  const machineModelsCx = useContext(MachineModelsContext);
  const machines = useMachinesPortalStore((state) => state.machines);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(Step.Creation);

  const [start, setStart] = useState(subMonths(startOfToday(), 3));

  const [end, setEnd] = useState(endOfHour(new Date()));

  const [filterNotes, setFilterNotes] = useState<string[]>();
  const [summaries, setSummaries] = useState<IGatherSummary[]>();
  const [evals, setEvals] = useState<IEvalModelResult[]>();

  const [requirePreview, setRequirePreview] = useState(true);

  const [hash, setHash] = useState<string>();
  const [model, setModel] = useState<ITrainModelsRequest>({
    ...ModelHelper.getDefaultDataFilter(start, end),
    machineID: props.refModel?.machineID,
    model_types: props.refModel ? [props.refModel.type] : [],
  });

  const updateMetadata = useCallback(
    (payload: Partial<ITrainModelsRequest>, resetPreview = true) => {
      setModel((prev) => ({ ...prev, ...payload }));
      setRequirePreview((prev) => resetPreview || prev);
    },
    [setModel, setRequirePreview]
  );

  // update the start to the later of 3mo ago and the machine's last hw change date
  useEffect(() => {
    const threeMonthsAgo = subMonths(startOfToday(), 3);

    const machine = machines.find(
      (m) => m.machineID === props.refModel?.machineID
    );

    const nextStart = machine?.last_hardware_changed
      ? max([parseISO(machine.last_hardware_changed), threeMonthsAgo])
      : threeMonthsAgo;

    setStart(nextStart);
  }, [props.refModel?.machineID]);

  const validateModel = (config: {
    checkTypes: boolean;
    checkMinGroups: boolean;
  }) => {
    if (!model.ball_type) {
      NotifyHelper.error({
        message_md: 'Please select a ball type and try again.',
      });
      return false;
    }

    const machine = machines.find((m) => m.machineID === model.machineID);

    if (!machine) {
      NotifyHelper.error({
        message_md: 'Please select a valid machine and try again.',
      });
      return false;
    }

    const minStartDate = machine.last_hardware_changed
      ? parseISO(machine.last_hardware_changed)
      : undefined;

    if (minStartDate && isBefore(start, minStartDate)) {
      const startDS = format(start, LOCAL_DATETIME_FORMAT_SHORT, {
        timeZone: LOCAL_TIMEZONE,
      });

      const minStartDS = format(minStartDate, LOCAL_DATETIME_FORMAT_SHORT, {
        timeZone: LOCAL_TIMEZONE,
      });

      NotifyHelper.error({
        message_md: `Start date (${startDS}) cannot be earlier than last hardware changed date (${minStartDS}). Please check inputs and try again.`,
      });
      return false;
    }

    if (config.checkTypes) {
      if (!model.model_types || model.model_types.length === 0) {
        NotifyHelper.error({
          message_md: 'Please select at least one model type and try again.',
        });
        return false;
      }
    }

    if (config.checkMinGroups) {
      if (model.min_groups === undefined) {
        NotifyHelper.warning({
          message_md: `Please provide a valid min groups value and try again.`,
        });
        return false;
      }

      if (!summaries) {
        NotifyHelper.warning({
          message_md: `Please preview your query and try again.`,
        });
        return false;
      }

      const selectedTypes = ALLOWED_MODEL_TYPES.filter((m) =>
        model.model_types.includes(m.value)
      ).sort((a, b) => (a.min_groups > b.min_groups ? -1 : 1));

      const maxGroupType = selectedTypes[0];

      if (maxGroupType && summaries.length < maxGroupType.min_groups) {
        NotifyHelper.warning({
          message_md: `${maxGroupType.name} requires at least ${maxGroupType.min_groups} min groups, but your query only contains ${summaries.length}. Please deselect this model type or increase min groups and try again.`,
        });
        return false;
      }
    }

    return true;
  };

  const handleCreate = async () => {
    const isValid = validateModel({ checkTypes: true, checkMinGroups: true });

    if (!isValid || loading) {
      return;
    }

    setLoading(true);

    NotifyHelper.warning({
      message_md:
        'Creating model(s), please wait as this may take some time...',
    });

    try {
      // results includes eval of active model + new models
      const results = await machineModelsCx.trainModels(model);
      setStep(Step.Review);
      setEvals(results);
    } finally {
      setLoading(false);
    }
  };

  const handlePreview = async () => {
    const isValid = validateModel({ checkTypes: false, checkMinGroups: false });

    if (!isValid) {
      return;
    }

    // set summary to undefined so that the graph components are destroyed and recreated
    setLoading(true);
    setSummaries(undefined);

    NotifyHelper.warning({
      message_md:
        'Generating preview, please wait as this may take some time...',
    });

    try {
      const nextHash = MiscHelper.hashify(model);
      setHash(nextHash);

      const result =
        await AdminMachineModelsService.getInstance().summarizeData(model);

      setSummaries(result?.summaries);
      setFilterNotes(result?.filterNotes);
      setRequirePreview(false);
    } finally {
      setLoading(false);
    }
  };

  const mergeProps: IFullDialog = {
    identifier: COMPONENT_NAME,
    width: RADIX.DIALOG.WIDTH.XL,
    title: 'Create Model',
    loading: machineModelsCx.loading,
    content:
      step === Step.Review ? (
        <MachineModelCreationResults modelEvals={evals} />
      ) : (
        <MachineModelCreationForm
          loading={loading}
          model={model}
          summaries={summaries}
          filterNotes={filterNotes}
          start={start}
          onSetStart={setStart}
          end={end}
          onSetEnd={setEnd}
          updateMetadata={updateMetadata}
        />
      ),
    buttons: [
      {
        invisible: step !== Step.Review,
        label: 'Back',
        onClick: () => {
          setStep(Step.Creation);
          setEvals(undefined);
        },
      },
      {
        icon: summaries ? <UpdateIcon /> : <DownloadIcon />,
        label: summaries ? 'Update Preview' : 'Fetch Preview',
        color: RADIX.COLOR.SUCCESS,
        disabled: loading || MiscHelper.hashify(model) === hash,
        invisible: step !== Step.Creation,
        onClick: handlePreview,
      },
      {
        icon: <PlusIcon />,
        label: 'common.create',
        color: RADIX.COLOR.ACCENT,
        variant: 'solid',
        tooltip: summaries
          ? 'Please update preview first.'
          : 'Please fetch preview first.',
        disabled: loading || requirePreview,
        invisible: !!evals,
        onClick: handleCreate,
      },
    ],
    onClose: () => props.onClose(false),
  };

  return (
    <ErrorBoundary identifier={COMPONENT_NAME}>
      <CommonDialog {...mergeProps} />
    </ErrorBoundary>
  );
};

interface IFormProps {
  loading: boolean;
  model: ITrainModelsRequest;
  summaries?: IGatherSummary[];
  filterNotes?: string[];

  start: Date;
  onSetStart: (start: Date) => void;

  end: Date;
  onSetEnd: (end: Date) => void;

  updateMetadata: (
    payload: Partial<ITrainModelsRequest>,
    resetPreview?: boolean
  ) => void;
}

const MachineModelCreationForm = (props: IFormProps) => {
  const {
    end,
    filterNotes,
    loading,
    model,
    start,
    summaries,
    onSetEnd,
    onSetStart,
    updateMetadata,
  } = props;

  const machines = useMachinesPortalStore((s) => s.machines);

  const machine = machines.find((m) => m.machineID === model.machineID);
  const minStartDate = machine?.last_hardware_changed
    ? parseISO(machine.last_hardware_changed)
    : undefined;

  const selectMachineCallback = (machineID: string | undefined) => {
    const selectedMachine = machines.find((m) => m.machineID === machineID);

    if (!selectedMachine) {
      // shouldn't happen
      return;
    }

    const lastHardwareChanged = selectedMachine.last_hardware_changed
      ? parseISO(selectedMachine.last_hardware_changed)
      : undefined;

    /**
     * The below is supposed to set the start date to the last_hardware_changed date at the earliest.
     * This doesn't really work right now due to some issues with the Date Picker being unable to
     * have its value set programatically from a parent component (sometimes).
     *
     * The Date Picker will have to be refactored to be a properly controlled component
     * before this will behave has intended.
     */
    const safeStartDate =
      lastHardwareChanged && isBefore(start, lastHardwareChanged)
        ? lastHardwareChanged
        : start;

    onSetStart(safeStartDate);
    updateMetadata(
      {
        machineID: selectedMachine.machineID,
        ball_type: selectedMachine.ball_type,
        start_date: safeStartDate.toISOString(),
      },
      true
    );
  };

  const plotModes: PlotMode[][] = [
    ['std_vy', 'std_wx'],
    ['std_wy', 'std_wz'],
    ['std_break_x_in', 'std_break_z_in'],
  ];

  const machineOptions = useMemo(
    () =>
      machines.map((o) => ({
        label: MachineContextHelper.getMachineMenuLabel(o),
        value: o.machineID,
      })),
    [machines]
  );

  const breakInches =
    model.break_stdev_limit_ft !== undefined
      ? model.break_stdev_limit_ft * FT_TO_INCHES
      : undefined;

  return (
    <Flex direction="column" gap={RADIX.FLEX.GAP.MD}>
      <CommonFormGrid columns={3}>
        {/* row 1 */}
        <CommonSearchInput
          id="create-model-machineID"
          label="common.machine"
          options={machineOptions}
          values={model.machineID ? [model.machineID] : []}
          onChange={(machineIDs) => selectMachineCallback(first(machineIDs))}
        />
        <CommonSelectInput
          id="create-model-ball"
          name="ball_type"
          label="common.ball-type"
          disabled={loading}
          options={BALL_TYPE_OPTIONS}
          value={model.ball_type}
          onChange={(v) => updateMetadata({ ball_type: v as BallType })}
          optional
        />
        <CommonTextInput
          id="create-model-collectionID"
          label="Collection ID (optional)"
          name="collectionID"
          disabled={loading}
          value={model.collectionID}
          onChange={(v) => updateMetadata({ collectionID: v })}
          hint_md='Like "AAAAAAAA"'
          optional
        />
        {/* row 2 */}
        <CommonDateInput
          id="create-model-start"
          label="common.start-date"
          defaultValue={start}
          minDate={minStartDate}
          maxDate={end}
          onChange={(date) => {
            if (!date) {
              NotifyHelper.warning({
                message_md: t('common.check-inputs-msg'),
              });
              return;
            }

            onSetStart(date);
            updateMetadata({ start_date: date.toISOString() });
          }}
          showTime
        />
        <CommonDateInput
          id="create-model-end"
          label="common.end-date"
          defaultValue={end}
          minDate={start}
          maxDate={endOfToday()}
          onChange={(date) => {
            if (!date) {
              NotifyHelper.warning({
                message_md: t('common.check-inputs-msg'),
              });
              return;
            }

            onSetEnd(date);
            updateMetadata({ end_date: date.toISOString() });
          }}
          showTime
        />
        <CommonTextInput
          id="create-model-session"
          label="Session (optional)"
          name="session"
          disabled={loading}
          value={model.session}
          onChange={(v) => updateMetadata({ session: v })}
          hint_md='Like "AAAAAAAA-...-BBBB.X"'
          optional
        />
        {/* row 3 */}
        <CommonTextInput
          id="create-model-min-groups"
          label="Min Groups"
          type="number"
          name="min_groups"
          disabled={loading}
          value={model.min_groups?.toString()}
          placeholder="Type an integer value"
          onOptionalNumericChange={(e) => {
            updateMetadata({
              min_groups: e === undefined ? undefined : Math.round(e),
            });
          }}
          hint_md="Minimum number of unique target machine states. This value is ignored for previews."
        />
        <CommonTextInput
          id="create-model-max-groups"
          label="Max Groups"
          type="number"
          name="max_groups"
          disabled={loading}
          value={model.max_groups?.toString()}
          placeholder="Type an integer value"
          onOptionalNumericChange={(e) => {
            updateMetadata({
              max_groups: e === undefined ? undefined : Math.round(e),
            });
          }}
          hint_md="Maximum number of unique target machine states."
          optional
        />
        <CommonTextInput
          id="create-model-min-group-size"
          label="Min Group Size"
          type="number"
          name="min_group_size"
          disabled={loading}
          value={model.min_group_size?.toString()}
          placeholder="Type an integer value"
          onOptionalNumericChange={(e) => {
            updateMetadata({
              min_group_size: e === undefined ? undefined : Math.round(e),
            });
          }}
          hint_md="Minimum number of shots per target machine state."
          optional
        />
        {/* row 4 */}
        <CommonTextInput
          id="create-model-record-limit"
          label="Record Limit"
          type="number"
          name="limit"
          disabled={loading}
          value={model.limit?.toString()}
          placeholder="Type an integer value"
          onOptionalNumericChange={(v) => {
            updateMetadata({
              limit: v === undefined ? undefined : Math.round(v),
            });
          }}
          hint_md="Query limit on maximum number of records to use."
          optional
        />
        <CommonSelectInput
          id="create-model-aiming"
          label="Include Aiming Shots"
          name="aiming"
          disabled={loading}
          options={[
            { label: 'Yes', value: 'true' },
            { label: 'No', value: 'false' },
          ]}
          value={model.aiming?.toString()}
          onOptionalBooleanChange={(v) => {
            updateMetadata({
              aiming: v,
            });
          }}
          hint_md="Filter samples based on whether they were used for aiming or not."
          optional
        />
        <CommonSelectInput
          id="create-model-bp-shots"
          label="Include BP Shots"
          name="bp-shots"
          disabled={loading}
          options={[
            // * inverted labels vs values because training = not BP
            { label: 'Yes', value: 'false' },
            { label: 'No', value: 'true' },
          ]}
          value={model.training?.toString()}
          onBooleanChange={(v) => {
            updateMetadata({
              training: v,
            });
          }}
          hint_md="Filter samples based on whether they were for Batting Practice or not."
        />
        {/* advanced toggle */}
        <Box gridColumn="span 3">
          <CommonDetails summary="Advanced">
            <CommonFormGrid columns={3}>
              <CommonSliderInput
                id="create-model-max-std-v"
                label="Max StDev V (mph)"
                name="speed_stdev_limit_mph"
                disabled={loading}
                value={model.speed_stdev_limit_mph}
                onChange={(v) => updateMetadata({ speed_stdev_limit_mph: v })}
                hint_md={`Maximum Velo StDev of a Group ${HINT_DELIMITER} Current Value: ${model.speed_stdev_limit_mph?.toFixed(
                  1
                )}`}
                min={0}
                max={10}
                step={0.1}
              />

              <CommonSliderInput
                id="create-model-max-std-w"
                label="Max StDev W (rpm)"
                name="spin_stdev_limit_rpm"
                disabled={loading}
                value={model.spin_stdev_limit_rpm}
                onChange={(v) => updateMetadata({ spin_stdev_limit_rpm: v })}
                hint_md={`Maximum Spin StDev of a Group ${HINT_DELIMITER} Current Value: ${model.spin_stdev_limit_rpm?.toFixed(
                  0
                )}`}
                min={0}
                max={3000}
                step={10}
              />

              <CommonSliderInput
                id="create-model-max-std-break"
                label="Max StDev Break (in)"
                name="break_stdev_limit_in"
                disabled={loading}
                value={breakInches}
                onChange={(v) =>
                  updateMetadata({ break_stdev_limit_ft: v / FT_TO_INCHES })
                }
                hint_md={`Maximum Break StDev of a Group ${HINT_DELIMITER} Current Value: ${breakInches?.toFixed(
                  1
                )}`}
                min={0}
                max={30}
                step={0.1}
              />

              <Box gridColumn="span 3">
                <Heading size="2">Shot Quality Filters</Heading>
              </Box>

              <CommonCheckboxInput
                id="create-model-check_confidence"
                label="Shot Confidence"
                name="check_confidence"
                disabled={loading}
                checked={model.check_confidence}
                onChange={(v) => {
                  updateMetadata({
                    check_confidence: v,
                  });
                }}
                hint_md="Removes shots with any low confidence metrics"
              />
              <CommonCheckboxInput
                id="create-model-check_spin"
                label="Spin Filters"
                name="check_spin"
                disabled={loading}
                checked={model.check_spin}
                onChange={(v) => {
                  updateMetadata({
                    check_spin: v,
                  });
                }}
                hint_md="Removes shots with unrealistic spin components"
              />
              <CommonCheckboxInput
                id="create-model-check_breaks"
                label="Additional Break Filters"
                name="check_breaks"
                disabled={loading}
                checked={model.check_breaks}
                onChange={(v) => {
                  updateMetadata({
                    check_breaks: v,
                  });
                }}
                hint_md="Removes shots with unrealistic breaks"
              />
            </CommonFormGrid>
          </CommonDetails>
        </Box>
        <Box gridColumn="span 3">
          <Heading size={RADIX.HEADING.SIZE.SM} mb={RADIX.FLEX.GAP.INPUT}>
            Model Types
          </Heading>
          <CommonFormGrid columns={3}>
            {[0, 1, 2].map((iCol) => (
              <Box key={`model-types-${iCol}`}>
                <CommonChecklist
                  id={`create-model-types-${iCol}`}
                  name="types"
                  values={model.model_types ?? []}
                  disabled={loading}
                  options={ALLOWED_MODEL_TYPES.filter(
                    (_, i) => i % 3 === iCol
                  ).map((m) => ({
                    label: m.name,
                    value: m.value,
                    disabled:
                      env.identifier.includes('prod') && !m.production_enabled,
                    title: m.title,
                  }))}
                  onChange={(selected) => {
                    updateMetadata(
                      {
                        model_types: selected,
                      },
                      false
                    );
                  }}
                />
              </Box>
            ))}
          </CommonFormGrid>
        </Box>
      </CommonFormGrid>

      {summaries && (
        <Flex direction="column" gap="4">
          <CommonDetails summary="Preview" defaultOpen>
            <HistogramGroup mode="target_ms_group_size" groups={summaries} />
          </CommonDetails>

          {filterNotes && (
            <CommonDetails summary="Filter Notes">
              <ul>
                {filterNotes.map((note, i) => (
                  <li key={i}>{note}</li>
                ))}
              </ul>
            </CommonDetails>
          )}

          <CommonDetails summary="Variance">
            <Flex direction="column" gap={RADIX.FLEX.GAP.MD}>
              {plotModes.map((row, iRow) => (
                <Box key={`mode-row-${iRow}`}>
                  {row.map((mode, iMode) => (
                    <PlotGroupStd
                      key={`mode-${iRow}-${iMode}`}
                      mode={mode}
                      groups={summaries ?? []}
                    />
                  ))}
                </Box>
              ))}
            </Flex>
          </CommonDetails>
          <CommonDetails summary="Raw Data">
            <JsonViewer value={summaries} />
          </CommonDetails>
        </Flex>
      )}
    </Flex>
  );
};

const MachineModelCreationResults = (props: {
  modelEvals: IEvalModelResult[] | undefined;
}) => {
  if (!props.modelEvals) {
    return null;
  }

  return (
    <Flex direction="column" gap={RADIX.FLEX.GAP.MD}>
      <Heading size={RADIX.HEADING.SIZE.SM}>Results</Heading>
      <SummarizeModelMetrics metrics={props.modelEvals} />
      <CommonDetails summary="Raw Metrics">
        <Flex direction="column" gap={RADIX.FLEX.GAP.MD}>
          {props.modelEvals.map((m) => (
            <PrintMetric key={`print-metric-${m.model_id}`} metric={m} />
          ))}
        </Flex>
      </CommonDetails>
    </Flex>
  );
};
